import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { message } from "antd";
import Header from "../component/header";
import Footer from "../component/Footer";
import {
  CalendarDays,
  Clock3,
  Compass,
  Heart,
  MessageCircle,
  Search,
  Share2,
  Sparkles,
  Users,
} from "lucide-react";
import { UserDataContext } from "../context/User";
import BannerImg from "../image/backgroundbanner.png";

const API_BASE_URL = "";

const sortOptions = [
  { value: "recent", label: "Most recent activity" },
  { value: "popular", label: "Popularity" },
  { value: "alpha", label: "Alphabetical" },
];

function slugify(value = "") {
  return value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function stripHtml(value = "") {
  return value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function formatDate(value) {
  if (!value) return "Recently";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

function timeAgo(value) {
  if (!value) return "Recently";
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.max(1, Math.floor(diff / 60000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDate(value);
}

function authorName(author) {
  if (!author) return "LWBL Member";
  const fullName = [author.firstName, author.lastName].filter(Boolean).join(" ");
  return author.preferredName || fullName || author.memberId || "LWBL Member";
}

const Blogs = () => {
  const navigate = useNavigate();
  const { userData } = useContext(UserDataContext);
  const [messageApi, contextHolder] = message.useMessage();
  const [blogs, setBlogs] = useState([]);
  const [genres, setGenres] = useState([]);
  const [commentCounts, setCommentCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [themeFilter, setThemeFilter] = useState("all");
  const [recentOnly, setRecentOnly] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function fetchListingData() {
      try {
        setLoading(true);
        setError("");

        const [blogResponse, genreResponse] = await Promise.all([
          fetch("/api/blog/fetch?status=published&limit=80"),
          fetch("/api/genre/fetch?isActive=true&limit=80"),
        ]);

        const [blogResult, genreResult] = await Promise.all([
          blogResponse.json(),
          genreResponse.json(),
        ]);

        if (!blogResult.success) {
          throw new Error(blogResult.message || "Blogs could not be loaded.");
        }

        const activeBlogs = (blogResult.blogs || []).filter((blog) => blog.status !== "archived");
        const activeGenres = genreResult.success ? genreResult.data || [] : [];

        if (!isMounted) return;
        setBlogs(activeBlogs);
        setGenres(activeGenres);

        const counts = {};
        await Promise.all(
          activeBlogs.slice(0, 36).map(async (blog) => {
            try {
              const response = await fetch(`/api/comments/fetch/blog/${blog._id}`);
              const result = await response.json();
              counts[blog._id] = Number.isFinite(result.total)
                ? result.total
                : Array.isArray(result.comments)
                  ? result.comments.length
                  : 0;
            } catch {
              counts[blog._id] = 0;
            }
          })
        );
        if (isMounted) setCommentCounts(counts);
      } catch (err) {
        if (isMounted) setError(err.message || "Unable to load blogs.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchListingData();
    return () => {
      isMounted = false;
    };
  }, []);

  const themeCards = useMemo(() => {
    const counts = new Map();
    blogs.forEach((blog) => {
      blog.genres?.forEach((genre) => {
        if (!genre?.name) return;
        const key = genre._id || genre.name;
        const current = counts.get(key) || {
          id: key,
          name: genre.name,
          slug: genre.slug || slugify(genre.name),
          count: 0,
          latest: null,
          image: null,
        };
        current.count += 1;
        if (!current.latest || new Date(blog.updatedAt || blog.createdAt) > new Date(current.latest)) {
          current.latest = blog.updatedAt || blog.createdAt;
          current.image = blog.coverImage;
        }
        counts.set(key, current);
      });
    });

    genres.forEach((genre) => {
      const key = genre._id || genre.name;
      if (!counts.has(key)) {
        counts.set(key, {
          id: key,
          name: genre.name,
          slug: genre.slug || slugify(genre.name),
          count: 0,
          latest: null,
          image: null,
        });
      }
    });

    return Array.from(counts.values())
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
      .slice(0, 8);
  }, [blogs, genres]);

  const filteredBlogs = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

    return blogs
      .filter((blog) => {
        if (recentOnly && new Date(blog.updatedAt || blog.createdAt).getTime() < oneWeekAgo) return false;
        if (themeFilter !== "all" && !blog.genres?.some((genre) => (genre.slug || slugify(genre.name)) === themeFilter)) {
          return false;
        }
        if (!normalizedQuery) return true;
        return `${blog.title} ${blog.description} ${authorName(blog.author)} ${blog.genres?.map((genre) => genre.name).join(" ") || ""}`
          .toLowerCase()
          .includes(normalizedQuery);
      })
      .sort((a, b) => {
        if (sortBy === "alpha") return a.title.localeCompare(b.title);
        if (sortBy === "popular") {
          const aScore = (commentCounts[a._id] || 0) + (a.likesCount || 0) + (a.viewsCount || 0);
          const bScore = (commentCounts[b._id] || 0) + (b.likesCount || 0) + (b.viewsCount || 0);
          return bScore - aScore;
        }
        return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
      });
  }, [blogs, commentCounts, query, recentOnly, sortBy, themeFilter]);

  const featured = filteredBlogs[0] || blogs[0];
  const newThisWeek = blogs.filter(
    (blog) => Date.now() - new Date(blog.updatedAt || blog.createdAt).getTime() <= 7 * 24 * 60 * 60 * 1000
  ).length;
  const memberCount = new Set(blogs.map((blog) => blog.author?._id || blog.author?.memberId).filter(Boolean)).size;

  const openBlog = (blog) => {
    navigate(`/blogs/${blog.slug}`);
  };

  const updateBlogCounts = (blogId, patch) => {
    setBlogs((current) =>
      current.map((blog) => (blog._id === blogId ? { ...blog, ...patch } : blog))
    );
  };

  const handleLike = async (event, blog) => {
    event.stopPropagation();

    if (!userData?.id) {
      navigate("/login", { state: { from: "/blogs" } });
      return;
    }

    try {
      const response = await fetch(`/api/blog/like/${blog._id}`, {
        method: "POST",
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to update like");
      }

      updateBlogCounts(blog._id, {
        likesCount: result.likesCount,
        hasLiked: result.hasLiked,
      });
      messageApi.success(result.hasLiked ? "Added to liked blogs" : "Like removed");
    } catch (err) {
      messageApi.error(err.message || "Unable to update like");
    }
  };

  const handleShare = async (event, blog) => {
    event.stopPropagation();

    const shareUrl = `${window.location.origin}/lwbl/blogs/${blog.slug}`;

    try {
      const response = await fetch(`/api/blog/share/${blog._id}`, {
        method: "POST",
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to share blog");
      }

      updateBlogCounts(blog._id, { sharesCount: result.sharesCount });

      if (navigator.share) {
        await navigator.share({
          title: blog.title,
          text: blog.description,
          url: shareUrl,
        });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        messageApi.success("Share link copied");
      } else {
        messageApi.success("Share counted");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        messageApi.error(err.message || "Unable to share blog");
      }
    }
  };

  return (
    <div className="App min-h-screen bg-[#f5f8f8] text-left">
      {contextHolder}
      <Header />
      <main>
        <section
          className="relative overflow-hidden bg-[#0f1718] px-4 pb-12 pt-32 text-white"
          style={{
            backgroundImage: `linear-gradient(90deg, rgba(8, 15, 16, 0.92), rgba(8, 15, 16, 0.6)), url(${BannerImg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-end">
            <div>
              <p className="font-outfit text-xs uppercase tracking-[0.25em] text-[#00F0FB]">Community Blogs</p>
              <h1 className="mt-4 max-w-4xl font-cinzel text-4xl leading-tight md:text-6xl">
                Recent stories, lessons, and artist notes from LWBL members
              </h1>
              <p className="mt-5 max-w-3xl font-outfit text-base leading-relaxed text-white/82 md:text-lg">
                Browse active blogs by theme, scan recent discussions, and jump into the conversations shaping the creative community.
              </p>

              <div className="mt-7 grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-white/15 bg-white/10 p-4 backdrop-blur">
                  <p className="font-cinzel text-2xl">{blogs.length}</p>
                  <p className="font-outfit text-sm text-white/75">Active Blogs</p>
                </div>
                <div className="rounded-lg border border-white/15 bg-white/10 p-4 backdrop-blur">
                  <p className="font-cinzel text-2xl">{newThisWeek}</p>
                  <p className="font-outfit text-sm text-white/75">New This Week</p>
                </div>
                <div className="rounded-lg border border-white/15 bg-white/10 p-4 backdrop-blur">
                  <p className="font-cinzel text-2xl">{memberCount}</p>
                  <p className="font-outfit text-sm text-white/75">Members Engaged</p>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/15 bg-white/10 p-3 backdrop-blur">
              {featured ? (
                <button
                  type="button"
                  onClick={() => openBlog(featured)}
                  className="group relative block h-[310px] w-full overflow-hidden rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-[#00F0FB]"
                  aria-label={`Open featured blog ${featured.title}`}
                >
                  <img
                    src={`${API_BASE_URL}${featured.coverImage || BannerImg}`}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-black/88 via-black/30 to-transparent" />
                  <span className="absolute left-5 top-5 rounded-full bg-[#00F0FB] px-3 py-1 font-outfit text-xs font-bold text-black">
                    Latest Highlight
                  </span>
                  <span className="absolute bottom-5 left-5 right-5">
                    <span className="font-outfit text-sm text-white/75">{timeAgo(featured.updatedAt || featured.createdAt)}</span>
                    <span className="mt-2 block font-cinzel text-2xl leading-tight text-white">{featured.title}</span>
                    <span className="mt-2 block font-outfit text-sm leading-6 text-white/78">
                      {stripHtml(featured.description).slice(0, 130)}
                      {stripHtml(featured.description).length > 130 ? "..." : ""}
                    </span>
                  </span>
                </button>
              ) : (
                <div className="flex h-[310px] items-center justify-center rounded-lg bg-white/10 font-outfit text-white/75">
                  No featured blog yet.
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-8">
          <div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <p className="font-outfit text-sm text-[#5f6c6d]">Switch categories quickly or open a full category hub.</p>
              <h2 className="mt-1 font-cinzel text-3xl text-[#101617]">Explore Categories</h2>
            </div>
            <Link
              to="/community"
              className="inline-flex w-fit items-center gap-2 rounded-full bg-black px-5 py-3 font-outfit text-sm font-bold text-white"
            >
              <Users size={17} />
              Join the discussion
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {themeCards.map((theme) => (
              <article key={theme.id} className="overflow-hidden rounded-lg border border-[#dce6e6] bg-white shadow-sm">
                <Link
                  to={`/blogs/theme/${theme.slug}`}
                  state={{ themeName: theme.name }}
                  className="group block"
                >
                  <div className="relative h-32 overflow-hidden bg-[#0f1718]">
                    <img
                      src={`${API_BASE_URL}${theme.image || BannerImg}`}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover opacity-80 transition duration-500 group-hover:scale-105"
                    />
                    <span className="absolute inset-0 bg-gradient-to-t from-black/100 to-transparent" />
                    <span className="absolute bottom-3 left-4 right-4 font-cinzel text-xl leading-tight text-white">
                      {theme.name}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 font-outfit text-sm text-[#5f6c6d]">
                    <span>{theme.count} active blog{theme.count === 1 ? "" : "s"}</span>
                    <span>{theme.latest ? timeAgo(theme.latest) : "No posts yet"}</span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-12">
          <div className="mb-6 rounded-lg border border-[#dce6e6] bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_220px_220px_auto] lg:items-center">
              <label className="relative">
                <span className="sr-only">Search blogs</span>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667273]" size={18} />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="w-full rounded-lg border border-[#d6dfdf] bg-white py-3 pl-10 pr-3 font-outfit text-sm outline-none focus:border-[#00C3FF]"
                  placeholder="Search by title, author, or theme"
                />
              </label>

              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                className="rounded-lg border border-[#d6dfdf] bg-white px-3 py-3 font-outfit text-sm outline-none focus:border-[#00C3FF]"
                aria-label="Sort blogs"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>

              <select
                value={themeFilter}
                onChange={(event) => setThemeFilter(event.target.value)}
                className="rounded-lg border border-[#d6dfdf] bg-white px-3 py-3 font-outfit text-sm outline-none focus:border-[#00C3FF]"
                aria-label="Filter by theme"
              >
                <option value="all">All categories</option>
                {themeCards.map((theme) => (
                  <option key={theme.id} value={theme.slug}>{theme.name}</option>
                ))}
              </select>

              <label className="inline-flex items-center gap-3 rounded-lg border border-[#d6dfdf] bg-[#fbfdfd] px-4 py-3 font-outfit text-sm text-[#2c3637]">
                <input
                  type="checkbox"
                  checked={recentOnly}
                  onChange={(event) => setRecentOnly(event.target.checked)}
                  className="h-4 w-4 accent-[#00C3FF]"
                />
                New this week
              </label>
            </div>
          </div>

          <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-outfit text-sm text-[#5f6c6d]">Default view is sorted by most recent activity.</p>
              <h2 className="mt-1 font-cinzel text-3xl text-[#101617]">Active Blogs</h2>
            </div>
            <p className="font-outfit text-sm text-[#5f6c6d]">
              Showing {filteredBlogs.length} of {blogs.length}
            </p>
          </div>

          {loading && (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="h-96 animate-pulse rounded-lg bg-white shadow-sm" />
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-6 font-outfit text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && filteredBlogs.length === 0 && (
            <div className="rounded-lg border border-dashed border-[#b9c8c9] bg-white p-10 text-center">
              <Sparkles className="mx-auto text-[#00A6C8]" size={34} />
              <h3 className="mt-4 font-cinzel text-2xl text-[#111]">No blogs match this view</h3>
              <p className="mt-2 font-outfit text-[#5f6c6d]">Try clearing filters or searching for another theme.</p>
            </div>
          )}

          {!loading && !error && filteredBlogs.length > 0 && (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filteredBlogs.map((blog) => {
                const comments = commentCounts[blog._id] || 0;
                const excerpt = stripHtml(blog.description || blog.content || "").slice(0, 180);
                const primaryTheme = blog.genres?.[0];
                const primaryThemeSlug = primaryTheme?.slug || slugify(primaryTheme?.name || "");

                return (
                  <article
                    key={blog._id}
                    className="group flex h-full flex-col overflow-hidden rounded-lg border border-[#dce6e6] bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl focus-within:ring-2 focus-within:ring-[#00C3FF]"
                  >
                    <button
                      type="button"
                      onClick={() => openBlog(blog)}
                      className="relative h-52 w-full overflow-hidden text-left"
                      aria-label={`View blog ${blog.title}`}
                    >
                      <img
                        src={`${API_BASE_URL}${blog.coverImage || BannerImg}`}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                      {primaryTheme?.name && (
                        <span className="absolute left-3 top-3 rounded-full bg-[#00F0FB] px-3 py-1 font-outfit text-xs font-bold text-black">
                          {primaryTheme.name}
                        </span>
                      )}
                    </button>

                    <div className="flex flex-1 flex-col p-5">
                      <div className="mb-3 flex items-center justify-between gap-3 text-xs font-outfit text-[#667273]">
                        <span className="inline-flex items-center gap-1">
                          <Clock3 size={14} />
                          {timeAgo(blog.updatedAt || blog.createdAt)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays size={14} />
                          {formatDate(blog.createdAt)}
                        </span>
                      </div>

                      <button type="button" onClick={() => openBlog(blog)} className="text-left focus:outline-none">
                        <h3 className="font-cinzel text-xl leading-tight text-[#101617] group-hover:text-[#007f99]">
                          {blog.title}
                        </h3>
                      </button>

                      <p className="mt-3 font-outfit text-sm text-[#5f6c6d]">
                        By Member - {blog.author?.memberId || "LWBL"} | {blog.author?.occupation || authorName(blog.author)}
                      </p>
                      <p className="mt-4 flex-1 font-outfit text-sm leading-relaxed text-[#374344]">
                        {excerpt}
                        {excerpt.length >= 180 ? "..." : ""}
                      </p>

                      <div className="mt-5 grid grid-cols-3 gap-2 text-xs font-outfit text-[#465354]">
                        <span className="inline-flex items-center gap-1 rounded-md bg-[#eef8f8] px-2 py-2">
                          <MessageCircle size={14} />
                          {comments} comments
                        </span>
                        <button
                          type="button"
                          onClick={(event) => handleLike(event, blog)}
                          className={`inline-flex items-center gap-1 rounded-md px-2 py-2 text-left transition ${
                            blog.hasLiked
                              ? "bg-[#ffe7ea] text-[#b4233a]"
                              : "bg-[#f6f3ee] hover:bg-[#fff1d8]"
                          }`}
                          aria-label={`Like ${blog.title}`}
                        >
                          <Heart size={14} />
                          {blog.likesCount || 0} likes
                        </button>
                        <button
                          type="button"
                          onClick={(event) => handleShare(event, blog)}
                          className="inline-flex items-center gap-1 rounded-md bg-[#f2f5fa] px-2 py-2 text-left transition hover:bg-[#e8eef9]"
                          aria-label={`Share ${blog.title}`}
                        >
                          <Share2 size={14} />
                          {blog.sharesCount || 0} shares
                        </button>
                      </div>

                      <div className="mt-5 flex flex-wrap gap-2">
                        {primaryTheme?.name && (
                          <Link
                            to={`/blogs/theme/${primaryThemeSlug}`}
                            state={{ themeName: primaryTheme.name }}
                            className="inline-flex items-center gap-2 rounded-full border border-[#c7d4d5] px-4 py-2 font-outfit text-sm font-semibold text-[#2c3637] hover:border-[#00C3FF]"
                          >
                            <Compass size={15} />
                            Theme
                          </Link>
                        )}
                        <button
                          className="rounded-full border border-[#c7d4d5] px-4 py-2 font-outfit text-sm font-semibold text-[#2c3637] hover:border-[#00C3FF]"
                          type="button"
                          onClick={() => openBlog(blog)}
                        >
                          Comment
                        </button>
                        <button
                          className="rounded-full bg-[#00F0FB] px-4 py-2 font-outfit text-sm font-bold text-black"
                          type="button"
                          onClick={() => openBlog(blog)}
                        >
                          View Full Blog
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Blogs;
